import React from 'react';
import {TablePagination, useTheme} from '@mui/material';
import {DropResult} from "@hello-pangea/dnd";
import {styled} from '@mui/material/styles';
import useTableParams from './useTableParams';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableHeader from "./parts/TableHeader";
import TableFilters from "./parts/TableFilters";
import {FilterConfig} from "./parts/filterInputs/FilterRenderer";
import TableContent from "./parts/TableContent";

const StyledContainer = styled('div')({
    overflowX: 'auto',
});

export interface EntityTableHeaderProps {
    key: string;
    label?: string;
    sortable?: boolean;
    filtered?: boolean;
    filterConfig?: FilterConfig;
    render?: (value: any, entity?: any) => React.ReactNode;
}

interface EntityTableProps {
    title?: string | React.ReactNode;
    controls?: React.ReactNode[];
    headers: EntityTableHeaderProps[];
    entities: any[];
    entitiesCount: number;
    indexes?: boolean;
    paged?: boolean;
    showLoading?: boolean;
    tableId?: string;
    draggable?: boolean;
    onDragEnd?: (result: DropResult) => void;
    renderAccordion?: (row: any) => React.ReactNode;
    isMobile?: boolean;
}

const EntityTable: React.FC<EntityTableProps> = ({
                                                     title,
                                                     controls,
                                                     headers,
                                                     entities,
                                                     entitiesCount,
                                                     indexes = false,
                                                     paged = true,
                                                     showLoading = true,
                                                     tableId = 'default',
                                                     draggable = false,
                                                     onDragEnd,
                                                     renderAccordion,
                                                     isMobile
                                                 }) => {
    const {
        filters,
        removeFilter,
        setFilter,
        sortDirections,
        sortKeys,
        setSort,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage
    } = useTableParams(tableId);
    const theme = useTheme();
    const isMobileTheme = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobileOrDefault = isMobile ?? isMobileTheme;

    return (
        <StyledContainer>
            <TableHeader
                title={title}
                controls={controls}
                isMobile={isMobileOrDefault}
            />

            <TableFilters
                isMobile={isMobileOrDefault}
                headers={headers}
                filters={filters}
                removeFilter={removeFilter}
                setFilter={setFilter}
                sortDirections={sortDirections}
                sortKeys={sortKeys}
                setSort={setSort}
            />

            <TableContent
                isMobile={isMobileOrDefault}
                entities={entities}
                headers={headers}
                sortKeys={sortKeys}
                sortDirections={sortDirections}
                setSort={setSort}
                indexes={indexes}
                page={paged ? page : 0}
                rowsPerPage={paged ? rowsPerPage : entities.length}
                showLoading={showLoading}
                draggable={draggable}
                onDragEnd={onDragEnd ?? (result => console.log(result))}
                renderAccordion={renderAccordion}
            />

            {paged && (
                <TablePagination
                    component="div"
                    count={entitiesCount}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => setRowsPerPage(e.target.value)}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Řádků na stránku"
                />
            )}
        </StyledContainer>
    );
};

export default EntityTable;
